import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CustomisableSection } from '@shared/models/customisable-section';
import { MenuList } from '@shared/models/menu-list';
import { CustomImagePipe } from '@shared/pipes/custom-image.pipe';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { ISiteSettings, PlatformProviderType } from '@shared/shared/interfaces/site-settings.interfaces';
import { CustomisableSectionsService } from '@shared/shared/services/customisable-sections.service';
import { LanguageService } from '@shared/shared/services/language-service';
import { RecaptchaService } from '@shared/shared/services/recaptcha.service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { USER_LOCAL_STORAGE_KEY } from 'app.config';
import { NavigationService } from 'core/services/navigation.service';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'global-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    NgIf,
    CustomImagePipe,
    NgFor,
    SafeHtmlPipe,
    RouterModule,
    AccessibleClickDirective,
    TranslateModule,
  ],
})
export class FooterComponent implements OnInit {
  public platformProviderTypes = PlatformProviderType;
  public siteSettings$: Observable<ISiteSettings>;
  public isReCaptchaFooter$: Observable<boolean>;
  public footerSection$: Observable<CustomisableSection>;
  public reCaptchaSection$: Observable<CustomisableSection>;
  public preloginFooterSection$: Observable<CustomisableSection>;
  public secondaryMenuList$: Observable<MenuList[]>;
  public cookiesComponentData: { default: { dcrPolicyCode: string } };
  public policyCode: string;
  public siteLanguage = 'en-gb';
  public isImageLoaded: boolean;
  @Input() public dcrCookieLanguage = 'en-gb';
  @Input() public userAuthenticated$: Observable<boolean>;

  constructor(
    private customisableSectionsService: CustomisableSectionsService,
    private siteSettingsService: SiteSettingsService,
    private languageService: LanguageService,
    private reCaptchaService: RecaptchaService,
    private navigationService: NavigationService,
    private router: Router,
    public siteConfService: SiteConfigService
  ) {
    this.siteSettings$ = this.siteSettingsService.fetchSiteSettings();
    this.cookiesComponentData = this.siteConfService.getComponentData('cookies');
  }

  public ngOnInit() {
    this.isReCaptchaFooter$ = this.reCaptchaService.getReCaptcha();
    const isUserLoggedIn = !!localStorage.getItem(USER_LOCAL_STORAGE_KEY);
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((siteSettings: ISiteSettings) => !!siteSettings.id),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.siteLanguage = this.languageService.preparingSiteLanguageAttribute(siteSettings);
      });

    const lang = this.siteLanguage.includes('pt')
      ? 'pt-br'
      : this.siteLanguage.includes('es')
        ? 'es'
        : this.dcrCookieLanguage;
    this.policyCode =
      this.cookiesComponentData?.default?.dcrPolicyCode && this.dcrCookieLanguage === lang
        ? this.cookiesComponentData?.default?.dcrPolicyCode
        : 'b6231a84';

    const sections$ = this.languageService.languageUpdated.pipe(
      startWith(''),
      switchMap(() =>
        this.customisableSectionsService.getCustomisableSections([
          'footercontent',
          'recaptcha_banner',
          'prelogin_footercontent',
        ])
      ),
      shareReplay(1)
    );

    if (!isUserLoggedIn) {
      this.secondaryMenuList$ = combineLatest([
        this.navigationService.getNavigationLinks('content-outside-footer'),
        this.navigationService.getNavigationLinks('footer'),
      ]).pipe(
        map(([contentOutside, footer]) => {
          return [...contentOutside, ...footer];
        })
      );
    } else {
      this.secondaryMenuList$ = this.navigationService.getNavigationLinks('footer');
    }

    this.footerSection$ = sections$.pipe(map((sections) => (sections.length ? sections[0] : null)));
    this.reCaptchaSection$ = sections$.pipe(map((sections) => (sections.length ? sections[1] : null)));
    this.preloginFooterSection$ = sections$.pipe(map((sections) => (sections.length ? sections[2] : null)));
  }

  public navigate(link: string) {
    this.router.navigate([link]);
  }
}
